// Rapport d'usinage — matches the cooperative paper ledger format
// (CADESA-style side-by-side "Entrée Brousse" vs "Fabrication"
// table, with refraction-aware Ecart / Freinte and a Points Divers
// block for échantillon + déchets).
//
// All numbers are derived from the existing UsinageDetail payload
// (assignments + qdlAssignments + lots) so the panel + PDF + Excel
// exporters all consume the same RapportData shape.
import type {
  CouplingLoadView,
  PlannedLotRow,
  UsinageDetail,
  UsinageLotRow,
} from "@/features/milling/api/types"

// One row of the left-hand "Entrée Brousse" table. Mirrors the
// columns on the paper: Date · Fournisseur · N° CCC · Nature ·
// Poids · Refract°.
//
// Queue de Lots appear here too — same shape, but `cccNumber` is
// replaced by the QDL's lot number (QDLs have no CCC since they're
// internal virtual chargements) and `nature` is the QDL's product
// type (defaults to the usinage's defaultProductType when unset).
export interface RapportEntreeRow {
  kind: "LOAD" | "QUEUE_LOT"
  date: string | null         // ISO 8601 date
  fournisseur: string         // cooperative name (LOAD) / "Queue de Lot" (QDL)
  cccNumber: string           // DeliveryForm BoL number (LOAD) / lot number (QDL)
  nature: string              // product type, e.g. "CACAO"
  poidsKg: number             // raw brousse weight (netWeight × bag share for LOAD)
  refractKg: number           // refraction deduction (LOAD only — 0 for QDLs)
}

// One row of the right-hand "Fabrication" table. Date · N° du lot
// · Poids · Type produit.
//
// Queue de Lots are aggregated into a single row labelled
// "QUEUE (N sacs)" because the paper format shows them collapsed —
// only the operator-facing identity that matters is "this is the
// queue", and the bag count goes in the label.
export interface RapportFabricationRow {
  date: string | null
  numeroLot: string           // lot number for LOTs, "QUEUE (N sacs)" for the aggregated QDL row
  poidsKg: number
  typeProduit: string         // product type / certification label
  // Kind discriminator for styling; the row label already says
  // "QUEUE" so the FE doesn't need this for text, but the exporters
  // use it to tone QDL rows differently.
  kind: "LOT" | "QUEUE_LOT"
}

// One row of the per-planned-lot Freinte table. The Freinte rapport
// is the post-fabrication reconciliation between what the operator
// engaged into each Lot Planned and what they actually milled out.
//
// `actualKg` and `ecartKg` are null while the planned entry is still
// pending (operator hasn't fabricated yet); the on-screen table
// renders them as dashes until the lot is produced.
export interface RapportFreinteRow {
  plannedLotId: string             // server-minted UUID
  plannedLabel: string             // e.g. "LOT PLANNED 1"
  fabricatedLotNumber: string | null
  plannedKg: number                // Σ(cells.bags × source.avgBagKg)
  actualKg: number | null
  ecartKg: number | null           // plannedKg − actualKg, when fabricated
}

// Summary block under the Freinte table. Aggregates across every
// planned lot — pending entries contribute 0 to actualKg, so the
// totals are accurate even mid-fabrication.
//
// `wasteKg` is the operator-reported milling waste (déchet) for the
// whole usinage, captured once on the COMPLETED view. It enters the
// reconciliation as the third subtraction so the equation closes
// against what the operator actually measured:
//
//   Ecart   = totalPlannedKg − totalActualKg − wasteKg
//   Freinte = Ecart / totalPlannedKg × 100
export interface RapportFreinteSummary {
  totalPlannedKg: number
  totalActualKg: number
  wasteKg: number
  ecartKg: number
  freintePct: number
}

export interface RapportFreinteBlock {
  rows: RapportFreinteRow[]
  summary: RapportFreinteSummary
}

export interface RapportData {
  usinageNumber: string
  operator: string
  // Date range covered by entrée brousse arrivals — used in the
  // header band "USINAGE N° XYZ DU date1 au date2". Falls back to
  // the usinage's own startDate when no chargements have arrivedAt.
  dateRange: { from: string; to: string }

  entreeRows: RapportEntreeRow[]
  fabricationRows: RapportFabricationRow[]
  freinte: RapportFreinteBlock
}

export function buildRapport(
  usinage: UsinageDetail,
  loads: CouplingLoadView[],
): RapportData {
  const defaultProductType =
    usinage.defaultProductType ?? "CACAO"

  // Entrée brousse — one row per LOAD assignment + one row per QDL
  // assignment. Weights are scaled by the bag share so an
  // assignment that only takes part of a chargement contributes
  // proportionally.
  const entreeRows: RapportEntreeRow[] = []

  for (const a of usinage.assignments ?? []) {
    const load = a.load
    const totalBags = load?.bagsUnloaded ?? 0
    const share = totalBags > 0 ? (a.bagsAssigned ?? 0) / totalBags : 0
    const view = loads.find(
      (l) => l.sourceKind === "LOAD" && l.loadId === a.loadId,
    )
    entreeRows.push({
      kind: "LOAD",
      date: load?.arrivedAt ?? null,
      fournisseur:
        view?.cooperativeName ??
        load?.Orders?.groupId?.name ??
        "—",
      cccNumber:
        view?.deliverySlip ??
        load?.DeliveryForms?.billOfLadingNumber ??
        `#${load?.loadNumber ?? a.loadId}`,
      nature: defaultProductType,
      poidsKg: (load?.netWeight ?? 0) * share,
      refractKg: (load?.totalRefractionKg ?? 0) * share,
    })
  }

  for (const a of usinage.qdlAssignments ?? []) {
    const view = loads.find(
      (l) => l.sourceKind === "QUEUE_LOT" && l.loadId === a.queueLotId,
    )
    const avg = a.avgBagKg ?? view?.avgBagKg ?? 0
    entreeRows.push({
      kind: "QUEUE_LOT",
      date: null,
      fournisseur: "Queue de Lot",
      cccNumber: view?.loadNumber ?? `#${a.queueLotId}`,
      nature: defaultProductType,
      poidsKg: (a.bagsAssigned ?? 0) * avg,
      refractKg: 0,
    })
  }

  // Fabrication — one row per FULL lot, plus a single aggregated
  // row for all Queue de Lots produced by this run (matches the
  // paper's "QUEUE (N sacs)" convention).
  const lots = usinage.lots ?? []
  const fullLots = lots.filter((l) => l.kind === "FULL")
  const queueLots = lots.filter((l) => l.kind === "QUEUE")

  const fabricationRows: RapportFabricationRow[] = fullLots.map((l) => ({
    date: lotDate(l),
    numeroLot: l.lotNumber,
    poidsKg: l.netKg ?? 0,
    typeProduit: defaultProductType,
    kind: "LOT",
  }))

  if (queueLots.length > 0) {
    const totalBags = queueLots.reduce((acc, l) => acc + (l.bags ?? 0), 0)
    const totalKg = queueLots.reduce((acc, l) => acc + (l.netKg ?? 0), 0)
    fabricationRows.push({
      date: lotDate(queueLots[queueLots.length - 1]),
      numeroLot: `QUEUE (${totalBags} sacs)`,
      poidsKg: totalKg,
      typeProduit: defaultProductType,
      kind: "QUEUE_LOT",
    })
  }

  return {
    usinageNumber: usinage.usinageNumber,
    operator: usinage.operator,
    dateRange: computeDateRange(entreeRows, usinage),
    entreeRows,
    fabricationRows,
    freinte: buildFreinteBlock(
      usinage.lotsPlanned ?? [],
      lots,
      usinage.wasteKg ?? 0,
    ),
  }
}

// Builds the per-planned-lot Freinte rows + summary by joining each
// PlannedLot against its produced ExporterLots row via plannedLotRef.
// Pending entries surface as planned-only rows; fabricated entries
// surface the actual netKg + per-row ecart (planned − actual). The
// run-wide déchet only enters the summary, not the per-row ecart —
// operators report it once at the end of the run and there's no
// per-lot breakdown.
function buildFreinteBlock(
  planned: PlannedLotRow[],
  lots: UsinageLotRow[],
  wasteKg: number,
): RapportFreinteBlock {
  const fabricatedByPlannedRef = new Map<string, UsinageLotRow>()
  for (const l of lots) {
    if (l.plannedLotRef) fabricatedByPlannedRef.set(l.plannedLotRef, l)
  }

  const rows: RapportFreinteRow[] = planned.map((p) => {
    const fab = fabricatedByPlannedRef.get(p.id)
    const plannedKg = p.estimatedWeightKg
    const actualKg = fab?.netKg ?? null
    return {
      plannedLotId: p.id,
      plannedLabel: p.label,
      fabricatedLotNumber: fab?.lotNumber ?? null,
      plannedKg,
      actualKg,
      ecartKg: actualKg != null ? plannedKg - actualKg : null,
    }
  })

  const totalPlannedKg = rows.reduce((a, r) => a + r.plannedKg, 0)
  const totalActualKg = rows.reduce((a, r) => a + (r.actualKg ?? 0), 0)
  const ecartKg = totalPlannedKg - totalActualKg - wasteKg
  const freintePct = totalPlannedKg > 0 ? (ecartKg / totalPlannedKg) * 100 : 0

  return {
    rows,
    summary: {
      totalPlannedKg,
      totalActualKg,
      wasteKg,
      ecartKg,
      freintePct,
    },
  }
}

// ExporterLots doesn't carry a per-lot fabrication date on the
// detail embed yet; the cooperative paper uses one date per row, so
// we return null and fall back to the usinage-level date in the
// caller. Kept as a function so we can swap in a per-lot date once
// the backend emits one.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function lotDate(_lot: UsinageLotRow): string | null {
  return null
}

function computeDateRange(
  entree: RapportEntreeRow[],
  usinage: UsinageDetail,
): { from: string; to: string } {
  const dates = entree
    .map((r) => r.date)
    .filter((d): d is string => !!d)
    .map((d) => d.slice(0, 10))
    .sort()
  if (dates.length === 0) {
    const fallback = (usinage.startDate ?? "").slice(0, 10)
    return { from: fallback, to: fallback }
  }
  return { from: dates[0], to: dates[dates.length - 1] }
}
